import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { blokkoColors } from "../../theme/blokkoColors";
import LoadingBackdrop from "../LoadingBackdrop";

import AuthContext from "../../context/AuthContext";
import { fetchData } from "../../services/apiService";
import AdminFields from "../adminFields/AdminFields";
import {
  setApplyChanges,
  setReset,
  setWasUsed,
} from "../adminFields/AdminFields.slice";
import { useSelector, useDispatch } from "react-redux";
import DateField from "../dateField/DateField";
import moment from "moment";
import {
  getLocalTzOffsetMinutes,
  localCalendarDateToYyyymmdd,
} from "../../services/dateUtils";
import CurrencySelectField from "../selectField/CurrencySelectField";

/** Only entries shaped like a daily report (date + holdings). Skips null and nested sub-report objects. */
function getDailyClearingReportEntries(reports) {
  if (!reports || reports.report == null) return [];
  const list = Array.isArray(reports.report)
    ? reports.report
    : [reports.report];
  return list.filter(
    (item) =>
      item &&
      item.date != null &&
      item.holdings != null &&
      item.holdings.holding != null,
  );
}

const ClearingSettlementReportForm = ({
  setSnackOpen,
  setMessageOpen,
  setSeverity,
}) => {
  const dispatch = useDispatch();
  const { token, acquirerData } = useContext(AuthContext);
  const [currency, setCurrency] = useState(acquirerData?.currency_code || "");
  const [reportDate, setReportDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hide, setHide] = useState(true);
  const [reportData, setReportData] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const {
    applyChanges,
    acquirerId,
    isoID: storeIsoID,
    wasUsed,
    reset,
    acquirerCurrencyId,
  } = useSelector((state) => state.adminFields);

  useEffect(() => {
    if (reportDate) {
      setEndDate("");
    }
  }, [reportDate]);

  useEffect(() => {
    if (reset) {
      dispatch(setReset(false));
      setCurrency(acquirerData?.currency_code || "");
    }
  }, [reset]);

  useEffect(() => {
    if (applyChanges) {
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(true));
      setCurrency(acquirerCurrencyId || "");
    }
  }, [applyChanges]);

  useEffect(() => {
    if (wasUsed && storeIsoID === "" && acquirerId === "") {
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(false));
      setCurrency(acquirerData?.currency_code || "");
    }
  }, [storeIsoID, acquirerId]);

  const handleDateChange = (event) => {
    setReportDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setRetryCount(0);

    const timestamp = new Date().toISOString();
    const data = {
      version: "04.09.01",
      acquirer: {
        callbackURL:
          "https://webhook.site/dd4eadec-dab5-4397-9754-c45738f2fb9e",
        timeStamp: timestamp,
      },
      fiat: {
        currencyCode: currency,
      },
      report: {
        // Fechas de calendario local; el back agrupa por día local usando tzOffsetMinutes.
        startDate: localCalendarDateToYyyymmdd(reportDate),
        endDate: localCalendarDateToYyyymmdd(endDate),
        tzOffsetMinutes: getLocalTzOffsetMinutes(reportDate),
      },
    };

    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      acquirerName: acquirerData.acquirerName,
      acquirerID: wasUsed ? acquirerId : acquirerData.acquirerId,
    };

    try {
      const response = await fetchData({
        // endpoint: "/report/periodic_daily-clearing-settlement",
        endpoint: "/periodic_daily-clearing-settlement",
        data,
        headers,
        token,
      });
      if (response && response.data && response.data.blokko) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const checkResponse = await fetchData({
          // endpoint: "/report/periodic_daily-clearing-settlement",
          endpoint: "/periodic_daily-clearing-settlement",
          data,
          headers,
          token,
        });
        setRetryCount(1);
        if (checkResponse && checkResponse.data.reports) {
          setReportData(checkResponse.data.reports);
          setHide(false);
          setSnackOpen(true);
          setMessageOpen("Report generated successfully!");
          setSeverity("success");
        } else {
          setSnackOpen(true);
          setMessageOpen(
            "Report is still being generated. Please try again later.",
          );
          setSeverity("info");
        }
      } else if (response && response.data.reports) {
        setReportData(response.data.reports);
        setHide(false);
        setSnackOpen(true);
        setMessageOpen("Report generated successfully!");
        setSeverity("success");
      } else {
        setSnackOpen(true);
        setReportData("");
        setMessageOpen("No transaction for this date");
        setSeverity("error");
      }
    } catch (error) {
      setSnackOpen(true);
      setMessageOpen("No transaction for this date");
      setReportData("");
      setSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    const headers = [
      "Date",
      "Amount",
      "Trans. Count",
      "Settlement",
      "Holding Name",
      "Holding ID",
      "Holding Amount",
      "Holding Trans. Count",
      "Holding Settlement",
      "Brand Name",
      "Brand ID",
      "Brand Amount",
      "Brand Trans. Count",
      "Brand Settlement",
      "Brand MDR Fee",
      "Brand Proc. Fee",
      "Merchant Name",
      "Merchant ID",
      "Merchant Settlement",
      "Merchant Amount",
      "Merchant Trans. Count",
      "Merchant Proc. Fee",
      "Terminal ID",
      "Terminal Exchange Name",
      "Terminal Settlement",
      "Terminal Amount",
      "Terminal Trans. Count",
      "Terminal Proc. Fee",
    ];

    const formatDecimal = (value) => {
      if (typeof value === "string") {
        const cleanedValue = value.replace(",", ".").replace(/[^0-9.]/g, "");

        if ((cleanedValue.match(/\./g) || []).length > 1) {
          return "0.0000";
        }

        const number = parseFloat(cleanedValue);

        if (!isNaN(number)) {
          return number.toFixed(4);
        }
      }

      if (typeof value === "number") {
        return value.toFixed(4);
      }

      return "0.0000";
    };
    const rows = [];

    getDailyClearingReportEntries(reportData).forEach((report) => {
      const date = `${report.date.toString().substring(4, 6)}/${report.date
        .toString()
        .substring(6, 8)}/${report.date.toString().substring(0, 4)}`;

      report.holdings.holding.forEach((holding) => {
        holding.groups.group.forEach((group) => {
          group.merchants.merchant.forEach((merchant) => {
            if (merchant.terminals && merchant.terminals.terminal.length > 0) {
              merchant.terminals.terminal.forEach((terminal) => {
                rows.push([
                  date,
                  formatDecimal(reportData.acquirer.transactionsAmount),
                  reportData.acquirer.transactionsCount,
                  formatDecimal(reportData.acquirer.amountDue),
                  holding.merchantHolding,
                  holding.merchantHoldingID,
                  formatDecimal(holding.transactionsAmount),
                  holding.transactionsCount,
                  formatDecimal(holding.amountDue),
                  group.merchantGroup,
                  group.merchantGroupID,
                  formatDecimal(group.transactionsAmount),
                  group.transactionsCount,
                  formatDecimal(group.amountDue),
                  formatDecimal(group.txMDR || "0.000"),
                  formatDecimal(group.txProcessing || "0.000"),
                  merchant.name,
                  merchant.ID,
                  formatDecimal(merchant.amountDue),
                  formatDecimal(merchant.transactionsAmount),
                  merchant.transactionsCount,
                  formatDecimal(merchant.txProcessing),
                  terminal.terminalID,
                  terminal.exchangeName || "N/A",
                  formatDecimal(terminal.amountDue),
                  formatDecimal(terminal.transactionsAmount),
                  terminal.transactionsCount,
                  formatDecimal(terminal.txProcessing),
                ]);
              });
            } else {
              rows.push([
                date,
                formatDecimal(reportData.acquirer.transactionsAmount),
                reportData.acquirer.transactionsCount,
                formatDecimal(reportData.acquirer.amountDue),
                holding.merchantHolding,
                holding.merchantHoldingID,
                formatDecimal(holding.transactionsAmount),
                holding.transactionsCount,
                formatDecimal(holding.amountDue),
                group.merchantGroup,
                group.merchantGroupID,
                formatDecimal(group.transactionsAmount),
                group.transactionsCount,
                formatDecimal(group.amountDue),
                formatDecimal(group.txMDR || "0.000"),
                formatDecimal(group.txProcessing || "0.000"),
                merchant.name,
                merchant.ID,
                formatDecimal(merchant.amountDue),
                formatDecimal(merchant.transactionsAmount),
                merchant.transactionsCount,
                formatDecimal(merchant.txProcessing),
                "",
                "",
                "",
                "",
                "",
                "",
              ]);
            }
          });
        });
      });
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "ClearingSettlementReport.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderHoldingsTable = (reportData) => {
    return (
      <Accordion style={{ marginTop: "20px" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Report Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="h6">Acquirer Details</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Trans. Count</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>MDR Fee</TableCell>
                  <TableCell>Settlement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{reportData.acquirer.transactionsCount}</TableCell>
                  <TableCell>
                    {reportData.acquirer.transactionsAmount}
                  </TableCell>
                  <TableCell>{reportData.acquirer.txProcessing}</TableCell>
                  <TableCell style={{ color: blokkoColors.primary }}>
                    {reportData.acquirer.amountDue}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {getDailyClearingReportEntries(reportData).map((item, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Report Date:{" "}
                  {`${item.date
                    .toString()
                    .replace(/^(\d{4})(\d{2})(\d{2})$/, "$2/$3/$1")}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {item.holdings.holding.map((holding, holdingIndex) => (
                  <Accordion key={holdingIndex}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        {holding.merchantHolding} - {holding.merchantHoldingID}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Trans. Count</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>MDR Fee</TableCell>
                              <TableCell>Settlement</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>{holding.transactionsCount}</TableCell>
                              <TableCell>
                                {holding.transactionsAmount}
                              </TableCell>
                              <TableCell>{holding.txProcessing}</TableCell>
                              <TableCell style={{ color: blokkoColors.primary }}>
                                {holding.amountDue}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Typography variant="h6" style={{ marginTop: "20px" }}>
                        Brands
                      </Typography>
                      {holding.groups.group.map((group, groupIndex) => (
                        <Accordion key={groupIndex}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                              {group.merchantGroup} - {group.merchantGroupID}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TableContainer component={Paper}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Trans. Count</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>MDR Fee</TableCell>
                                    <TableCell>Settlement</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  <TableRow>
                                    <TableCell>
                                      {group.transactionsCount}
                                    </TableCell>
                                    <TableCell>
                                      {group.transactionsAmount}
                                    </TableCell>
                                    <TableCell>{group.txProcessing}</TableCell>
                                    <TableCell style={{ color: blokkoColors.primary }}>
                                      {group.amountDue}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>

                            <Typography
                              variant="h6"
                              style={{ marginTop: "20px" }}
                            >
                              Merchants
                            </Typography>
                            {group.merchants.merchant.map(
                              (merchant, merchantIndex) => (
                                <Accordion key={merchantIndex}>
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                  >
                                    <Typography>
                                      {merchant.name} - {merchant.ID}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <TableContainer component={Paper}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Trans. Count</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell>MDR Fee</TableCell>
                                            <TableCell>Settlement</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          <TableRow>
                                            <TableCell>
                                              {merchant.transactionsCount}
                                            </TableCell>
                                            <TableCell>
                                              {merchant.transactionsAmount}
                                            </TableCell>
                                            <TableCell>
                                              {merchant.txProcessing}
                                            </TableCell>
                                            <TableCell
                                              style={{ color: blokkoColors.primary }}
                                            >
                                              {merchant.amountDue}
                                            </TableCell>
                                          </TableRow>
                                        </TableBody>
                                      </Table>
                                    </TableContainer>

                                    <Typography
                                      variant="h6"
                                      style={{ marginTop: "20px" }}
                                    >
                                      Terminals
                                    </Typography>
                                    {merchant.terminals &&
                                    merchant.terminals.terminal.length > 0 ? (
                                      <TableContainer component={Paper}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Terminal ID</TableCell>
                                              <TableCell>Exchange</TableCell>
                                              <TableCell>
                                                Trans. Count
                                              </TableCell>
                                              <TableCell>Amount</TableCell>
                                              <TableCell>MDR Fee</TableCell>
                                              <TableCell>Settlement</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {merchant.terminals.terminal.map(
                                              (terminal, terminalIndex) => (
                                                <TableRow key={terminalIndex}>
                                                  <TableCell>
                                                    {terminal.terminalID}
                                                  </TableCell>
                                                  <TableCell>
                                                    {terminal?.exchangeName || "N/A"}
                                                  </TableCell>
                                                  <TableCell>
                                                    {terminal.transactionsCount}
                                                  </TableCell>
                                                  <TableCell>
                                                    {
                                                      terminal.transactionsAmount
                                                    }
                                                  </TableCell>
                                                  <TableCell>
                                                    {terminal.txProcessing}
                                                  </TableCell>
                                                  <TableCell
                                                    style={{ color: blokkoColors.primary }}
                                                  >
                                                    {terminal.amountDue}
                                                  </TableCell>
                                                </TableRow>
                                              ),
                                            )}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        style={{ marginTop: "10px" }}
                                      >
                                        No terminals available.
                                      </Typography>
                                    )}
                                  </AccordionDetails>
                                </Accordion>
                              ),
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      <LoadingBackdrop loading={loading} retryCount={retryCount} />
      <AdminFields hideIsoField />
      <form onSubmit={handleSubmit} style={{ margin: "20px 0" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <CurrencySelectField
              name="currency"
              value={currency || ""}
              onChange={(event) => setCurrency(event.target.value)}
              label="Currency Code"
              required
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <DateField
              fullWidth
              id="start-date"
              name="start-date"
              label="Start Date"
              showTimezoneLabel
              value={reportDate}
              onChange={handleDateChange}
              minDate={moment().subtract(90, "days")}
              maxDate={moment().subtract(1, "days")}
              helperText="Select a date within the last 90 days"
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <DateField
              fullWidth
              id="end-date"
              name="end-date"
              label="End Date"
              showTimezoneLabel
              value={endDate}
              onChange={handleEndDateChange}
              minDate={
                reportDate !== ""
                  ? moment(reportDate)
                  : moment().subtract(90, "days")
              }
              maxDate={moment().subtract(1, "days")}
              helperText="Select a date within the last 90 days"
              required
            />
          </Grid>
          <Grid
            item
            xs={12}
            textAlign={isMobile ? "center" : "left"}
            style={{ marginTop: "20px" }}
          >
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={loading}
            >
              {loading ? "GENERATING..." : "GENERATE"}
            </Button>
            {reportData && (
              <Button
                variant="outlined"
                style={{ marginLeft: "10px" }}
                onClick={downloadCSV}
                disabled={hide || loading}
              >
                Download CSV
              </Button>
            )}
          </Grid>
        </Grid>
      </form>

      {!hide && !loading && reportData && renderHoldingsTable(reportData)}
    </>
  );
};

export default ClearingSettlementReportForm;
